import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Button,
  Typography,
  Grid,
  Paper,
  Alert,
  CircularProgress,
  Divider,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { PersonAdd, Edit, Delete, Person } from "@mui/icons-material";
import config from "../../config";

const baseUrl = config.baseURL;

// Define a limited, strict color palette based on the user's input/context
const PRIMARY_BLUE = "#2E4053"; // Used for buttons, highlights
const DARK_TEXT = "#1E293B"; // Used for headings
const LIGHT_BG = "#F7F9FC"; // Main page background
const CARD_BG = "#FFFFFF"; // Card background
const SECONDARY_TEXT = "#64748B"; // Text secondary color (soft grey/blue)

const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userLimitReached, setUserLimitReached] = useState(false);
  const [maxUsers, setMaxUsers] = useState(0);
  const [userCount, setUserCount] = useState(0);
  const [loggedInUser, setLoggedInUser] = useState(null);
  const navigate = useNavigate();

  const loggedInUserId = localStorage.getItem("userId");

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const token = localStorage.getItem("token");

        // 1️⃣ Fetch all users
        const usersRes = await fetch(`${baseUrl}/api/newUser`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const usersData = await usersRes.json();
        let allUsers = usersData.data || [];

        // Filter out any "master" users
        allUsers = allUsers.filter(
          (user) => !user.role?.toLowerCase().includes("master")
        );

        const currentUser = allUsers.find(
          (u) => u.id.toString() === loggedInUserId
        );
        const otherUsers = allUsers.filter(
          (u) => u.id.toString() !== loggedInUserId
        );

        setLoggedInUser(currentUser || null);
        setUsers(otherUsers);

        // 2️⃣ Fetch company's user count and subscription limit
        const limitRes = await fetch(`${baseUrl}/api/companyUserLimit`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const limitData = await limitRes.json();

        if (limitData) {
          const { userCount: currentUserCount, maxUsers: planMaxUsers } =
            limitData;
          setUserCount(currentUserCount);
          setMaxUsers(planMaxUsers);

          if (currentUserCount >= planMaxUsers) setUserLimitReached(true);
        }
      } catch (err) {
        console.error("Error fetching users or user limit", err);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [loggedInUserId]);

  const handleDelete = async (id) => {
    if (id.toString() === loggedInUserId) {
      alert("You cannot delete your own account");
      return;
    }

    if (!window.confirm("Are you sure you want to delete this user?")) return;

    try {
      await axios.delete(`${baseUrl}/api/newUser/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      alert("User deleted successfully.");
      setUsers(users.filter((user) => user.id !== id));
      setUserCount((prevCount) => prevCount - 1);
      if (userCount - 1 < maxUsers) setUserLimitReached(false);
    } catch (err) {
      console.error("Error deleting user", err);
      alert("Error deleting user. Check console for details.");
    }
  };

  const handleAddNewUser = () => {
    if (userLimitReached) {
      alert(
        `User limit reached (${userCount}/${maxUsers}). Please upgrade your plan.`
      );
    } else {
      navigate("/dashboard/addNewUser");
    }
  };

  const navigateToEdit = (id) => {
    navigate(`/dashboard/EditUserPage/${id}`);
  };

  return (
    <Box
      sx={{
        padding: { xs: "15px", md: "30px" },
        backgroundColor: LIGHT_BG, // Use strict BG color
        minHeight: "100vh",
      }}
    >
      <Typography
        variant="h4"
        gutterBottom
        sx={{ fontWeight: 600, color: DARK_TEXT, mb: 2 }} // Use strict DARK_TEXT
      >
        User Management
      </Typography>
      <Typography variant="body1" color={SECONDARY_TEXT} sx={{ mb: 3 }}>
        Manage users, permissions, and account limits for your organization.
      </Typography>

      {/* Action Button */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <Button
          variant="contained"
          startIcon={<PersonAdd />}
          sx={{
            backgroundColor: PRIMARY_BLUE, // Use strict PRIMARY_BLUE
            color: "#fff",
            "&:hover": { backgroundColor: "#1A2A38" }, // Darker shade of PRIMARY_BLUE
            padding: "10px 20px",
            fontWeight: "bold",
          }}
          onClick={handleAddNewUser}
          disabled={userLimitReached}
        >
          Add New User
        </Button>
        <Typography
          variant="subtitle1"
          fontWeight="medium"
          color={userLimitReached ? "error.main" : PRIMARY_BLUE} // Use PRIMARY_BLUE for limit text
        >
          Users: **{userCount} / {maxUsers}**
        </Typography>
      </Box>

      {/* Limit Alert (Kept existing alert colors as they are necessary for severity, 
          but removed blue background/text colors used previously) */}
      {userLimitReached && (
        <Alert
          severity="warning"
          sx={{
            marginBottom: "20px",
            backgroundColor: "#FFF3E0",
            color: "#FF9800",
            border: "1px solid #FF9800",
            fontWeight: "medium",
          }}
        >
          **User Limit Reached:** You have hit the maximum of {maxUsers} users.
          Please contact support to upgrade your plan.
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
          <CircularProgress color="primary" />
          <Typography sx={{ ml: 2, color: SECONDARY_TEXT }}>
            Loading user data...
          </Typography>
        </Box>
      ) : (
        <>
          {/* 1. Logged-in User Info (Distinct Text Block Above Cards) */}
          {loggedInUser && (
            <Box
              sx={{
                p: 3,
                mb: 4,
                borderLeft: `4px solid ${PRIMARY_BLUE}`, // Use strict PRIMARY_BLUE
                backgroundColor: CARD_BG, // Use white/light background for consistency
                borderRadius: "4px",
                boxShadow: 1, // Subtle shadow for distinction
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    color: DARK_TEXT, // Use strict DARK_TEXT
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Person sx={{ mr: 1, color: PRIMARY_BLUE }} />
                  {loggedInUser.firstname} {loggedInUser.lastname} (You)
                </Typography>
                <Typography
                  variant="body1"
                  color={SECONDARY_TEXT}
                  sx={{ mt: 0.5 }}
                >
                  Email: **{loggedInUser.email}** | Role: **
                  {loggedInUser.role.toUpperCase()}**
                </Typography>
              </Box>
              <Button
                variant="outlined"
                startIcon={<Edit />}
                onClick={() => navigateToEdit(loggedInUser.id)}
                sx={{
                  color: PRIMARY_BLUE,
                  borderColor: PRIMARY_BLUE,
                  "&:hover": { backgroundColor: "#F0F3F7" }, // Very subtle hover shade
                }}
              >
                Edit Your Profile
              </Button>
            </Box>
          )}

          <Divider sx={{ mb: 4 }} />

          <Typography variant="h5" sx={{ mb: 3, color: DARK_TEXT }}>
            Organization Users ({users.length})
          </Typography>

          {/* 2. Other Users Displayed as Larger Cards */}
          <Grid container spacing={4}>
            {users.map((user) => (
              <Grid item xs={12} sm={6} md={4} key={user.id}>
                <Paper
                  elevation={5}
                  sx={{
                    padding: "30px",
                    borderRadius: "8px",
                    backgroundColor: CARD_BG,
                    transition: "0.3s",
                    "&:hover": {
                      boxShadow: 8,
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{ color: DARK_TEXT, mb: 1, fontWeight: 600 }}
                  >
                    {user.firstname} {user.lastname}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ mb: 1, fontWeight: "medium", color: PRIMARY_BLUE }} // Use PRIMARY_BLUE for role
                  >
                    Role: {user.role.toUpperCase()}
                  </Typography>
                  <Typography
                    variant="body2"
                    color={SECONDARY_TEXT}
                    sx={{ mb: 2 }}
                  >
                    Email: {user.email}
                  </Typography>

                  <Divider sx={{ my: 2 }} />

                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<Edit />}
                      onClick={() => navigateToEdit(user.id)}
                      sx={{
                        backgroundColor: PRIMARY_BLUE,
                        "&:hover": { backgroundColor: "#1A2A38" },
                        flexGrow: 1,
                      }}
                    >
                      Edit
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Delete />}
                      onClick={() => handleDelete(user.id)}
                      sx={{
                        color: "#FFFFFF",

                        backgroundColor: "darkred",
                        "&:hover": {
                          backgroundColor: "#660000", // Darker red for hover state
                        },

                        flexGrow: 1,
                      }}
                    >
                      Delete
                    </Button>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </>
      )}
    </Box>
  );
};

export default UsersPage;
